import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  DollarSign,
  Users,
  Share2,
  CheckCircle,
  ArrowRight,
  Gift,
  TrendingUp,
  Heart,
} from "lucide-react";

const ReferralProgram = () => {
  const steps = [
    {
      step: 1,
      title: "Apply to Join",
      description: "Fill out a quick application telling us about yourself and how you plan to spread the word.",
      icon: Users,
    },
    {
      step: 2,
      title: "Get Your Unique Code",
      description: "Once approved, you'll receive your personal referral code and access to your partner dashboard.",
      icon: Gift,
    },
    {
      step: 3,
      title: "Share & Earn",
      description: "Share your code with your network. When someone signs up using your code, you earn $150!",
      icon: DollarSign,
    },
  ];

  const benefits = [
    {
      title: "$150 Per Referral",
      description: "Earn $150 for every person who signs up for an eligible program using your code.",
      icon: DollarSign,
    },
    {
      title: "Easy Tracking",
      description: "Access your personal dashboard to track referrals, earnings, and request payouts anytime.",
      icon: TrendingUp,
    },
    {
      title: "Flexible Payouts",
      description: "Request payouts via PayPal, Cash App, or Venmo. Payouts processed within 7-10 business days.",
      icon: Share2,
    },
    {
      title: "Make an Impact",
      description: "Help healthcare practices thrive while earning rewards. Your referrals transform patient care.",
      icon: Heart,
    },
  ];

  const programs = [
    {
      name: "CareRevive",
      description: "Practice Revival Program",
      price: "$750",
    },
    {
      name: "Foundations First",
      description: "Operational Excellence Program",
      price: "$1,250",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Referral Program - Earn by Helping Healthcare Practices | RNB Consulting</title>
        <meta
          name="description"
          content="Know a clinic or practice that could use help? Earn $150 for every referral that signs up for one of our healthcare consulting programs. Easy to join, easy to share."
        />
      </Helmet>

      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 gradient-hero opacity-95" />
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920')] bg-cover bg-center mix-blend-overlay opacity-20" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white mb-6">
              <Gift className="h-4 w-4" />
              <span className="text-sm font-medium">Partner Program</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Earn <span className="text-rnb-teal">$150</span> Per Referral
            </h1>
            
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-4">
              Know someone who could benefit from our healthcare consulting programs? 
              Share your referral code and earn money when they sign up!
            </p>
            
            <p className="text-lg text-rnb-teal/90 max-w-xl mx-auto mb-8">
              Make a positive impact on healthcare practices while building your income.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/referral-program/apply">
                <Button size="lg" className="btn-primary text-lg px-8 py-6">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-lg px-8 py-6">
                  Learn More
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-rnb-teal">$150</p>
                <p className="text-rnb-gray mt-1">Per Referral</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-rnb-blue">7-10</p>
                <p className="text-rnb-gray mt-1">Days to Payout</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-rnb-navy">∞</p>
                <p className="text-rnb-gray mt-1">No Earning Limit</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="py-20 bg-gradient-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
                How It Works
              </h2>
              <p className="text-lg text-rnb-gray max-w-2xl mx-auto">
                Getting started is simple. Follow these three easy steps to start earning.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((step, index) => (
                <div key={step.title || step.name || i} className="relative">
                  <Card className="h-full card-modern hover:shadow-lg transition-shadow">
                    <CardContent className="p-8 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-rnb-blue/10 text-rnb-blue mb-6">
                        <step.icon className="h-8 w-8" />
                      </div>
                      <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-rnb-teal text-white flex items-center justify-center font-bold">
                        {step.step}
                      </div>
                      <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-3">
                        {step.title}
                      </h3>
                      <p className="text-rnb-gray">{step.description}</p>
                    </CardContent>
                  </Card>
                  {index < steps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="h-8 w-8 text-rnb-teal" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
                Why Join Our Program?
              </h2>
              <p className="text-lg text-rnb-gray max-w-2xl mx-auto">
                We've made it rewarding and easy to earn by sharing programs that genuinely help healthcare practices.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={benefit.title || benefit.name || i} className="card-modern hover:shadow-lg transition-all group">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-rnb-teal/10 flex items-center justify-center mb-4 group-hover:bg-rnb-teal/20 transition-colors">
                      <benefit.icon className="h-6 w-6 text-rnb-teal" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-rnb-gray">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Milestone Bonuses */}
        <section className="py-20 bg-gradient-to-br from-amber-50 to-yellow-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-700 mb-4">
                <span className="text-xl">🎯</span>
                <span className="text-sm font-semibold">Milestone Bonuses</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
                Earn Even More with Milestone Bonuses!
              </h2>
              <p className="text-lg text-rnb-gray max-w-2xl mx-auto">
                On top of your $150 per referral, earn extra bonuses when you hit these milestones.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <Card className="card-modern text-center border-2 border-amber-200 hover:border-amber-400 transition-colors">
                <CardContent className="p-8">
                  <span className="text-5xl mb-4 block">🌟</span>
                  <h3 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                    5 Referrals
                  </h3>
                  <p className="text-sm text-rnb-gray mb-4">Rising Star</p>
                  <div className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 text-white font-bold text-2xl">
                    +$250
                  </div>
                  <p className="text-xs text-rnb-gray mt-4">Bonus added to your balance</p>
                </CardContent>
              </Card>

              <Card className="card-modern text-center border-2 border-amber-300 hover:border-amber-500 transition-colors scale-105 shadow-lg">
                <CardContent className="p-8">
                  <span className="text-5xl mb-4 block">🏆</span>
                  <h3 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                    10 Referrals
                  </h3>
                  <p className="text-sm text-rnb-gray mb-4">Champion Partner</p>
                  <div className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-2xl">
                    +$500
                  </div>
                  <p className="text-xs text-rnb-gray mt-4">Bonus added to your balance</p>
                </CardContent>
              </Card>

              <Card className="card-modern text-center border-2 border-amber-200 hover:border-amber-400 transition-colors">
                <CardContent className="p-8">
                  <span className="text-5xl mb-4 block">💎</span>
                  <h3 className="font-heading text-2xl font-bold text-rnb-navy mb-2">
                    20 Referrals
                  </h3>
                  <p className="text-sm text-rnb-gray mb-4">Elite Partner</p>
                  <div className="inline-block px-6 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold text-2xl">
                    +$1,000
                  </div>
                  <p className="text-xs text-rnb-gray mt-4">Bonus added to your balance</p>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-10">
              <p className="text-lg text-rnb-navy font-semibold">
                Total Potential: <span className="text-rnb-teal">$4,750</span> with just 20 referrals!
              </p>
              <p className="text-sm text-rnb-gray mt-2">
                (20 × $150 commissions) + $250 + $500 + $1,000 bonuses
              </p>
            </div>
          </div>
        </section>

        {/* Eligible Programs */}
        <section className="py-20 bg-rnb-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-4">
                Eligible Programs
              </h2>
              <p className="text-lg text-rnb-gray max-w-2xl mx-auto">
                Earn $150 when someone signs up for either of these signature programs using your referral code.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
              {programs.map((program, index) => (
                <Card key={program.title || program.name || program.label || index} className="card-modern text-center">
                  <CardContent className="p-8">
                    <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                      {program.name}
                    </h3>
                    <p className="text-rnb-gray text-sm mb-4">{program.description}</p>
                    <p className="text-3xl font-bold text-rnb-blue">{program.price}</p>
                    <div className="mt-4 flex items-center justify-center gap-2 text-rnb-teal">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">$150 Commission</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Make a Difference Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rnb-teal/10 text-rnb-teal mb-6">
                <Heart className="h-8 w-8" />
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-rnb-navy mb-6">
                More Than Just Earnings
              </h2>
              <p className="text-lg text-rnb-gray mb-6">
                When you refer someone to our programs, you&apos;re not just earning a commission—you&apos;re 
                helping transform healthcare practices and improve patient care across the country.
              </p>
              <p className="text-lg text-rnb-gray mb-6">
                Every practice that goes through our programs becomes more efficient, more profitable, 
                and better equipped to serve their patients. Your referrals create a ripple effect of 
                positive change in the healthcare industry.
              </p>
              <p className="text-xl font-semibold text-rnb-navy">
                Together, we&apos;re building healthier practices and healthier communities.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-rnb-navy to-rnb-blue">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Start Earning?
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Apply today and start sharing. There's no cost to join and no limit to how much you can earn!
            </p>
            <Link to="/referral-program/apply">
              <Button size="lg" className="bg-white text-rnb-navy hover:bg-gray-100 text-lg px-8 py-6">
                Apply to Join
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="mt-4 text-white/80 text-sm">
              Already a partner?{" "}
              <Link to="/partner/login" className="text-white underline hover:text-rnb-teal">
                Log in to your dashboard
              </Link>
            </p>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-8 text-center">
              Common Questions
            </h2>
            
            <div className="space-y-6">
              <div className="p-6 bg-rnb-light rounded-xl">
                <h3 className="font-semibold text-rnb-navy mb-2">When do I get paid?</h3>
                <p className="text-rnb-gray">You can request a payout anytime your balance reaches $50 or more. Payouts are processed within 7-10 business days via PayPal, Cash App, or Venmo.</p>
              </div>
              <div className="p-6 bg-rnb-light rounded-xl">
                <h3 className="font-semibold text-rnb-navy mb-2">Which programs are eligible?</h3>
                <p className="text-rnb-gray">You earn $150 commission for referrals to our CareRevive and Foundations First programs.</p>
              </div>
              <div className="p-6 bg-rnb-light rounded-xl">
                <h3 className="font-semibold text-rnb-navy mb-2">How long is my referral code valid?</h3>
                <p className="text-rnb-gray">Your referral code never expires! As long as you&apos;re an active partner, you can continue earning commissions.</p>
              </div>
              <div className="p-6 bg-rnb-light rounded-xl">
                <h3 className="font-semibold text-rnb-navy mb-2">Is there a limit to how many people I can refer?</h3>
                <p className="text-rnb-gray">No limits! Refer as many people as you&apos;d like. The more successful referrals, the more you earn.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ReferralProgram;
